import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../services/firebase";

export const useWeather = (lat, lon, refreshIntervalMs = 10 * 60 * 1000) => {
  const [weather, setWeather] = useState({
    temp: null,
    desc: null,
    code: 0,
    humidity: null,
    windSpeed: null,
    pressure: null,
    timezone: null,
    loading: true,
  });

  useEffect(() => {
    if (lat == null || lon == null) {
      setWeather((p) => ({ ...p, loading: false }));
      return;
    }

    let cancelled = false;
    let intervalId = null;

    const fetchWeather = async () => {
      try {
        const url =
          `https://api.open-meteo.com/v1/forecast` +
          `?latitude=${lat}` +
          `&longitude=${lon}` +
          `&current_weather=true` +
          `&hourly=relativehumidity_2m,surface_pressure` +
          `&timezone=auto`;

        const res = await fetch(url);

        if (!res.ok) {
          throw new Error("Weather request failed");
        }

        const data = await res.json();

        if (cancelled || !data?.current_weather) return;

        const current = data.current_weather;

        const weatherData = {
          temp:
            current.temperature != null
              ? Math.round(current.temperature)
              : null,
          desc: getWeatherDescription(current.weathercode),
          code: current.weathercode ?? 0,
          windSpeed:
            current.windspeed != null ? Math.round(current.windspeed) : null,
          humidity: data.hourly?.relativehumidity_2m?.[0] ?? null,
          pressure:
            data.hourly?.surface_pressure?.[0] != null
              ? Math.round(data.hourly.surface_pressure[0])
              : null,
          timezone: data.timezone || null,
          loading: false,
        };

        setWeather(weatherData);
        console.log(weatherData);
      } catch (error) {
        if (!cancelled) {
          setWeather((p) => ({ ...p, loading: false }));
        }
      }
    };

    // Initial fetch
    fetchWeather();

    // Set up interval for periodic updates
    intervalId = setInterval(fetchWeather, refreshIntervalMs);

    // Cleanup function
    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [lat, lon, refreshIntervalMs]);

  return weather;
};

export const useBuyerProfile = (buyerId) => {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!buyerId) return;

    let cancelled = false;

    const fetchProfile = async () => {
      try {
        const ref = doc(db, "buyerregistrations", buyerId);
        const snap = await getDoc(ref);

        if (!cancelled && snap.exists()) {
          setProfile(snap.data());
        }
      } catch (error) {
        // silent fail
      }
    };

    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [buyerId]);

  return profile;
};

function getWeatherDescription(code) {
  if (code === 0) return "Clear Sky";
  if (code <= 3) return "Partly Cloudy";
  if (code >= 45 && code <= 48) return "Foggy";
  if (code >= 51 && code <= 67) return "Rainy";
  if (code >= 71 && code <= 86) return "Snowy";
  if (code >= 95) return "Thunderstorm";
  return "Overcast";
}
